/**
 * Die Musterportfolios — welche es gibt und unter welcher Adresse.
 *
 * Ein reines Datenmodul: Die Kennungen sind dieselben wie in
 * `backend/app/services/musterportfolios.py` (`MUSTERPORTFOLIOS`), die
 * Adressen stehen ausgeschrieben in `app/_seo/seiten.ts`, `utils/appAdressen.ts`,
 * `App.jsx`, `backend/app/services/sitemap.py` und `llms_txt.py` —
 * `test_musterportfolios.py` hält alle gegen diese Liste. Die Zahlen kommen
 * vom Backend (`/api/public/musterportfolios`), die Texte aus
 * `musterportfolioTexte.ts`.
 */
import type { components } from '../../types/api.generated';

export const MUSTER_PFAD = '/model-portfolios';

/**
 * `t` dieser Seiten — breit genug für `fehlerText` (Vorgabe optional, Zahlen
 * und Texte als Werte) UND für `kennzahlAnzeige`/`anzahlText` (beliebige Werte).
 */
export type Uebersetzer = (key: string, vorgabe?: string, variablen?: Record<string, unknown>) => string;

export type MusterKennung =
  | 'world-equity'
  | 'world-portfolio-70-30'
  | '80-20'
  | '60-40'
  | '40-60'
  | 'permanent-portfolio'
  | 'all-weather'
  | 'golden-butterfly';

/** Reihenfolge = Reihenfolge auf der Übersicht (wie im Backend). */
export const MUSTER: readonly MusterKennung[] = [
  'world-equity',
  'world-portfolio-70-30',
  '80-20',
  '60-40',
  '40-60',
  'permanent-portfolio',
  'all-weather',
  'golden-butterfly',
];

export type MusterKurz = components['schemas']['MusterportfolioKurz'];
export type MusterDetail = components['schemas']['MusterportfolioDetail'];
export type MusterAnnahmen = components['schemas']['MusterportfolioAnnahmen'];
export type MusterZeitraum = components['schemas']['MusterportfolioZeitraum'];
export type MusterAlle = components['schemas']['MusterportfoliosResponse'];
export type MusterEines = components['schemas']['MusterportfolioResponse'];

/** Pfad ohne Sprach-Präfix, für `getLocalizedPath`. */
export function musterPfad(kennung: MusterKennung): string {
  return `${MUSTER_PFAD}/${kennung}`;
}

/** Die Kennung zu einer Adresse (`/de/deutsch/model-portfolios/60-40`) — `null` für jede andere. */
export function kennungDerAdresse(pathname: string): MusterKennung | null {
  const teile = pathname.replace(/\/+$/, '').split('/');
  if (teile.length < 2 || `/${teile[teile.length - 2]}` !== MUSTER_PFAD) return null;
  const kennung = teile[teile.length - 1];
  return (MUSTER as readonly string[]).includes(kennung) ? (kennung as MusterKennung) : null;
}

/** Der Zeitraum `zeitraum` eines Portfolios — `null`, wenn die Daten ihn nicht abdecken. */
export function zeitraumVon(p: MusterKurz, zeitraum: string): MusterZeitraum | null {
  return p.zeitraeume.find((z) => z.zeitraum === zeitraum) ?? null;
}

/**
 * Der erste Stand vom Server (`OeffentlicheSeite`): je Abruf-Schlüssel
 * (`alle` oder die Kennung) die Antwort — oder `null`, wenn das Backend noch
 * nichts gerechnet hat (503). Dann zeigen Server und Browser denselben Hinweis.
 */
const vorgelegt = new Map<string, MusterAlle | MusterEines | null>();

export function legeMusterportfoliosVor(schluessel: string, daten: MusterAlle | MusterEines | null): void {
  vorgelegt.set(schluessel, daten);
}

export function vorgelegteMusterportfolios(schluessel: string): { da: boolean; daten: MusterAlle | MusterEines | null } {
  return { da: vorgelegt.has(schluessel), daten: vorgelegt.get(schluessel) ?? null };
}
